import {
  Component,
  ChangeDetectionStrategy,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AuthService } from '@auth0/auth0-angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { HeaderComponent, SideNavComponent } from '@org/shared';
import { SessionService } from './core/auth/session.service';
import { SessionExpiredDialogComponent } from './core/auth/session-expired-dialog';

@Component({
  imports: [
    RouterModule,
    ToastModule,
    ProgressSpinnerModule,
    HeaderComponent,
    SideNavComponent,
    SessionExpiredDialogComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private errorSubscription?: Subscription;

  /**
   * Eagerly instantiated so its proactive expiry watcher starts running as
   * soon as the shell mounts — it decodes the access token and schedules
   * a silent refresh a couple of minutes before the exp claim fires.
   * The reactive 401 interceptor also depends on this service being alive.
   */
  private readonly session = inject(SessionService);

  /** True while the Auth0 SDK is initialising or processing a redirect callback */
  readonly isLoading = toSignal(this.auth.isLoading$, { initialValue: true });

  /** Only true once the SDK has confirmed an active session */
  readonly isAuthenticated = toSignal(this.auth.isAuthenticated$, { initialValue: false });

  /** Set when Auth0 returns an error during callback (e.g. access_denied, org not a member) */
  readonly authError = toSignal(this.auth.error$, { initialValue: null });

  ngOnInit(): void {
    this.errorSubscription = this.auth.error$.subscribe(error => {
      if (error) {
        // Auth0 errors carry extra properties beyond the base Error type
        const e = error as Error & { error?: string; error_description?: string };
        console.error('[Auth0]', e.error ?? e.message, e.error_description ?? '', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.errorSubscription?.unsubscribe();
  }
}
